import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cancelSubscription, fetchBillingStatus, type BillingStatusDetail } from '@/api/billing'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useMyHouseholds } from '@/hooks/useMyHouseholds'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { PlanPicker } from '@/components/billing/PlanPicker'
import { PlanPill } from '@/components/billing/PlanPill'
import { formatBillingDate, planStatusPill, planSummary, STATUS_LABEL } from '@/lib/planStatus'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/sheet'
import { SettingsPanel, SettingsRow, ROW_BUTTON } from './SettingsPanel'

function formatInr(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
}

export function PlanPanel() {
  const { role } = useCurrentHousehold()
  const { isPending: householdsLoading } = useMyHouseholds()
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
  })
  const [cancelling, setCancelling] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const roleResolving = role === null && (householdsLoading || !user)
  const isOwner = role === 'OWNER'
  const isNonOwner = role === 'MEMBER' || role === 'VIEWER'

  async function handleCancel() {
    setCancelling(true)
    try {
      await cancelSubscription(true)
      toast.success('Subscription will end at the close of the current period.')
      await queryClient.invalidateQueries({ queryKey: ['billing-status'] })
      await queryClient.invalidateQueries({ queryKey: ['entitlement'] })
    } catch {
      toast.error('Could not cancel. Please try again.')
    } finally {
      setCancelling(false)
    }
  }

  if (data?.status === 'sample') {
    return (
      <SettingsPanel id="plan" title="Plan & billing">
        <SettingsRow
          label="Sample households are free"
          hint="There's nothing to pay for here. Your own household's billing is in its own Settings."
        />
      </SettingsPanel>
    )
  }

  const sub = data?.subscription
  const showPlanPicker = !!data && data.status !== 'active'
  const showCancel = isOwner && !!sub && sub.status === 'ACTIVE' && !sub.cancelAtCycleEnd
  const payments = data?.payments ?? []
  const pill = data ? planStatusPill(data.status, data.trialEndsAt) : null
  const latest = payments.reduce<(typeof payments)[number] | null>(
    (a, p) => (!a || p.paidAt > a.paidAt ? p : a),
    null
  )

  return (
    <SettingsPanel
      id="plan"
      title="Plan & billing"
      overlays={
        showHistory && (
          <Sheet title="Payment history" onClose={() => setShowHistory(false)}>
            <PaymentList payments={payments} />
          </Sheet>
        )
      }
    >
      {roleResolving || isPending ? (
        <p className="py-4 text-sm text-[var(--fl-text-3)]">Loading…</p>
      ) : (
        <>
          {data && (
            <SettingsRow
              label={pill ? 'Plan' : (STATUS_LABEL[data.status] ?? data.status)}
              hint={pill ? planSummary(data) : undefined}
            >
              {pill && (
                <PlanPill tone={pill.tone} size="md">
                  {pill.label}
                </PlanPill>
              )}
            </SettingsRow>
          )}

          {isNonOwner && <SettingsRow label="Only the household owner can manage billing." />}

          {isOwner && showPlanPicker && (
            <div className="py-3">
              <h4 className="mb-3 text-sm font-semibold text-[var(--fl-heading)]">
                {data?.status === 'trialing' ? 'Subscribe' : 'Choose a plan'}
              </h4>
              <PlanPicker
                canPurchase
                onPurchased={() => queryClient.invalidateQueries({ queryKey: ['billing-status'] })}
              />
            </div>
          )}

          {showCancel && (
            <SettingsRow label="Cancel subscription" hint="You keep access until the period ends">
              <Button
                variant="outline"
                className={ROW_BUTTON}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Cancel subscription'}
              </Button>
            </SettingsRow>
          )}

          {latest && (
            <SettingsRow
              label="Payment history"
              hint={`Last payment ${formatBillingDate(latest.paidAt)}${latest.method ? ` · ${latest.method}` : ''}`}
            >
              <Button variant="outline" className={ROW_BUTTON} onClick={() => setShowHistory(true)}>
                View
              </Button>
            </SettingsRow>
          )}
        </>
      )}
    </SettingsPanel>
  )
}

function PaymentList({ payments }: { payments: BillingStatusDetail['payments'] }) {
  return (
    <ul className="divide-y divide-[var(--fl-border)] text-sm">
      {payments.map((p, i) => (
        <li key={i} className="flex items-center justify-between py-2">
          <span className="text-[var(--fl-text-2)]">
            {formatBillingDate(p.paidAt)}
            {p.method ? ` · ${p.method}` : ''}
          </span>
          <span className="flex items-center gap-3">
            <span className="font-medium text-[var(--fl-text-1)]">{formatInr(p.amount)}</span>
            {p.status !== 'CAPTURED' && (
              <span className="text-[var(--fl-text-3)]">{p.status.toLowerCase()}</span>
            )}
            {p.receiptUrl && (
              <a
                href={p.receiptUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--fl-primary)] underline"
              >
                receipt
              </a>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
